import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Empty, Segmented, Skeleton, Tooltip } from 'antd';
import { ClockCircleOutlined, FireOutlined, StopOutlined, TeamOutlined } from '@ant-design/icons';
import { callCenterReportsApi } from '../../utils/api';

const DAYS = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const fmt = (v, digits = 1) => (v == null ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: digits }));
const hourLabel = (h) => `${String(h).padStart(2, '0')}:00`;
const hourRange = (h) => `${String(h).padStart(2, '0')}:00 تا ${String((h + 1) % 24).padStart(2, '0')}:00`;

/**
 * value: مقداری که رنگ سلول را تعیین می‌کند. minCalls: حداقل تماس برای قابل‌اعتماد بودن نرخ‌ها.
 */
const METRICS = {
  calls: {
    label: 'تماس ورودی',
    color: '37, 99, 235',
    value: (c) => c.avg_calls,
    format: (v) => `${fmt(v)} در ساعت`,
    hint: 'میانگین تماس ورودی در هر بار تکرار آن روز هفته (حجم تماس)',
  },
  missed: {
    label: 'بی‌پاسخ',
    color: '220, 38, 38',
    value: (c) => c.missed,
    format: (v) => `${fmt(v, 0)} تماس`,
    hint: 'مجموع تماس‌های ورودی بی‌پاسخ در آن خانه در کل بازه',
  },
  miss_rate: {
    label: 'نرخ بی‌پاسخ',
    color: '234, 88, 12',
    value: (c) => (c.calls >= 3 ? c.miss_rate : null),
    format: (v) => `${fmt(v)}%`,
    hint: 'درصد تماس‌های بی‌پاسخ (فقط خانه‌های با حداقل ۳ تماس)',
  },
  load: {
    label: 'بار هر اپراتور',
    color: '147, 51, 234',
    value: (c) => (c.calls >= 3 ? c.calls_per_agent : null),
    format: (v) => `${fmt(v)} تماس به ازای هر اپراتور`,
    hint: 'میانگین تماس ورودی تقسیم بر میانگین اپراتورهای فعال؛ عدد بالا یعنی کمبود نیرو',
  },
};

const InsightTile = ({ icon, color, title, children, empty }) => (
  <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 shadow-sm p-3 flex gap-3 min-w-0">
    <span className="w-9 h-9 rounded-lg flex items-center justify-center text-base shrink-0" style={{ background: `${color}1f`, color }}>
      {icon}
    </span>
    <div className="min-w-0">
      <div className="custom-font text-xs text-gray-500 dark:text-gray-400">{title}</div>
      <div className="custom-font text-sm font-bold text-gray-800 dark:text-gray-100">
        {empty ? <span className="text-gray-300 font-normal">داده کافی نیست</span> : children}
      </div>
    </div>
  </div>
);

const HourlyHeatmapSection = ({ from, to }) => {
  const [metric, setMetric] = useState('calls');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true);
    setError(null);
    try {
      const res = await callCenterReportsApi.getHourlyHeatmap({ from, to });
      setData(res?.data ?? null);
    } catch (err) {
      setError(err?.message || 'خطا در دریافت نمودار ساعتی');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  const grid = useMemo(() => {
    const map = {};
    (data?.cells || []).forEach((c) => {
      map[`${c.dow}-${c.hour}`] = c;
    });
    return map;
  }, [data]);

  const cfg = METRICS[metric];
  const max = useMemo(
    () => Math.max(0, ...(data?.cells || []).map((c) => Number(cfg.value(c)) || 0)),
    [data, cfg]
  );

  const insights = useMemo(() => {
    const cells = data?.cells || [];
    if (!cells.length) return null;
    const best = (list, fn) => list.reduce((a, c) => (a == null || fn(c) > fn(a) ? c : a), null);

    const hourTotals = HOURS.map((h) => ({
      hour: h,
      calls: cells.filter((c) => c.hour === h).reduce((s, c) => s + c.calls, 0),
      missed: cells.filter((c) => c.hour === h).reduce((s, c) => s + c.missed, 0),
    }));
    const busiestHour = best(hourTotals, (x) => x.calls);
    const peak = best(cells, (c) => c.avg_calls);
    const worstMissed = best(cells.filter((c) => c.missed > 0), (c) => c.missed);
    const understaffed = cells
      .filter((c) => c.calls >= 3 && c.calls_per_agent >= 1)
      .sort((a, b) => b.calls_per_agent - a.calls_per_agent)
      .slice(0, 3);
    return { busiestHour, peak, worstMissed, understaffed };
  }, [data]);

  const cellColor = (c) => {
    const v = c ? Number(cfg.value(c)) : 0;
    if (!c || !v || !max) return undefined;
    const alpha = 0.12 + (v / max) * 0.88;
    return `rgba(${cfg.color}, ${alpha.toFixed(2)})`;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="custom-font text-lg font-bold text-gray-800 dark:text-gray-100 m-0 flex items-center gap-2">
            <ClockCircleOutlined className="text-blue-500" />
            نمودار تماس در ساعات روز
          </h2>
          <div className="custom-font text-xs text-gray-500 dark:text-gray-400">
            روز هفته × ساعت؛ برای تشخیص ساعات اوج، بی‌پاسخ‌ها و کمبود نیرو در شیفت‌بندی
          </div>
        </div>
        <Segmented
          value={metric}
          onChange={setMetric}
          className="custom-font"
          options={Object.entries(METRICS).map(([value, m]) => ({ value, label: m.label }))}
        />
      </div>

      {error && <Alert type="error" showIcon message={<span className="custom-font">{error}</span>} />}

      {loading && !data ? (
        <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 p-4">
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      ) : data && data.total_calls === 0 ? (
        <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 p-8">
          <Empty description={<span className="custom-font">در این بازه تماس ورودی ثبت نشده است</span>} />
        </div>
      ) : data ? (
        <div className={`space-y-3 transition-opacity ${loading ? 'opacity-60' : ''}`}>
          {insights && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <InsightTile icon={<FireOutlined />} color="#2563eb" title="شلوغ‌ترین ساعت روز" empty={!insights.busiestHour?.calls}>
                {insights.busiestHour && hourRange(insights.busiestHour.hour)}
                <span className="text-xs font-normal text-gray-400"> · {fmt(insights.busiestHour?.calls, 0)} تماس</span>
              </InsightTile>
              <InsightTile icon={<FireOutlined />} color="#0891b2" title="اوج حجم تماس" empty={!insights.peak}>
                {insights.peak && `${DAYS[insights.peak.dow]} ${hourLabel(insights.peak.hour)}`}
                <span className="text-xs font-normal text-gray-400"> · میانگین {fmt(insights.peak?.avg_calls)} تماس</span>
              </InsightTile>
              <InsightTile icon={<StopOutlined />} color="#dc2626" title="بیشترین بی‌پاسخ" empty={!insights.worstMissed}>
                {insights.worstMissed && `${DAYS[insights.worstMissed.dow]} ${hourLabel(insights.worstMissed.hour)}`}
                <span className="text-xs font-normal text-gray-400"> · {fmt(insights.worstMissed?.missed, 0)} تماس</span>
              </InsightTile>
              <InsightTile icon={<TeamOutlined />} color="#9333ea" title="نیاز به نیروی بیشتر" empty={!insights.understaffed.length}>
                {insights.understaffed.map((c) => (
                  <div key={`${c.dow}-${c.hour}`} className="leading-6">
                    {DAYS[c.dow]} {hourLabel(c.hour)}
                    <span className="text-xs font-normal text-gray-400">
                      {c.no_staff ? ' · بدون اپراتور' : ` · ${fmt(c.calls_per_agent)} تماس/اپراتور`}
                    </span>
                  </div>
                ))}
              </InsightTile>
            </div>
          )}

          <div className="rounded-xl border border-gray-100 dark:border-gray-700/60 bg-white dark:bg-gray-800/60 shadow-sm p-3 md:p-4">
            <div className="overflow-x-auto">
              <div className="min-w-[760px]" dir="ltr">
                <div className="flex">
                  <div className="w-20 shrink-0" />
                  {HOURS.map((h) => (
                    <div key={h} className="flex-1 text-center custom-font text-[10px] text-gray-400 pb-1">
                      {String(h).padStart(2, '0')}
                    </div>
                  ))}
                </div>
                {DAYS.map((day, dow) => (
                  <div key={day} className="flex items-stretch mb-[3px]">
                    <div className="w-20 shrink-0 custom-font text-xs text-gray-600 dark:text-gray-300 flex items-center justify-end pr-2" dir="rtl">
                      {day}
                    </div>
                    {HOURS.map((h) => {
                      const c = grid[`${dow}-${h}`];
                      const bg = cellColor(c);
                      return (
                        <Tooltip
                          key={h}
                          title={
                            <div className="custom-font text-xs leading-6" dir="rtl">
                              <div className="font-bold">{day} · {hourRange(h)}</div>
                              {c ? (
                                <>
                                  <div>مجموع تماس ورودی: {fmt(c.calls, 0)}</div>
                                  <div>میانگین در هر {day}: {fmt(c.avg_calls)}</div>
                                  <div>بی‌پاسخ: {fmt(c.missed, 0)} ({fmt(c.miss_rate)}%)</div>
                                  <div>میانگین اپراتور فعال: {fmt(c.avg_agents)}</div>
                                  <div>تماس به ازای هر اپراتور: {fmt(c.calls_per_agent)}</div>
                                </>
                              ) : (
                                <div>تماسی ثبت نشده</div>
                              )}
                            </div>
                          }
                        >
                          <div
                            className="flex-1 h-8 mx-[1.5px] rounded-[4px] bg-gray-100 dark:bg-gray-700/50"
                            style={bg ? { background: bg } : undefined}
                          />
                        </Tooltip>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between flex-wrap gap-2 custom-font text-[11px] text-gray-400">
              <span>{cfg.hint}</span>
              <span className="flex items-center gap-1" dir="ltr">
                کم
                <span className="inline-block w-24 h-2 rounded" style={{ background: `linear-gradient(to right, rgba(${cfg.color},0.12), rgba(${cfg.color},1))` }} />
                زیاد ({cfg.format(max)})
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default HourlyHeatmapSection;
